/**
 * Ninja Coin usage reference, shown on the plan pages so customers can see what
 * draws down their monthly allotment before they buy.
 *
 * Costs are **derived from `NINJA_CREDIT_COSTS`**, which mirrors the API's
 * authoritative `CREDIT_COST`. Never hard-code a number here: the API enforces
 * the charge, and a figure quoted here that disagrees with it is a billing
 * surprise. One Coin is one US dollar of list value.
 *
 * Update CHANGELOG.md when modifying this list.
 */
import {
  NINJA_CREDIT_COSTS,
  NINJA_SCAN_COST_RATES,
  NinjaCreditAction,
} from '@core/services/tier-access.service';

export interface TokenUsageItem {
  action: string;
  /** Coins drawn per run, from the canonical cost map. */
  cost: number;
  /** Plain-language note on how often this is typically used. */
  note: string;
}

/** Actions that spend Coins, cheapest first. */
export const TOKEN_USAGE_METERED: readonly TokenUsageItem[] = [
  {
    action: 'AI question about your findings',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.AI_QUERY_STANDARD],
    note: 'A typical day is a handful of questions',
  },
  {
    action: 'Dark web domain or email lookup',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.DARK_WEB_LOOKUP],
    note: 'On-demand lookup, for dark web add-on subscribers',
  },
  {
    action: 'AI question across your full findings history',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.AI_QUERY_LARGE_CONTEXT],
    note: 'Larger context, used occasionally',
  },
  {
    action: 'AI remediation code for a finding',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.AI_REMEDIATION_CODE],
    note: 'Generated per finding, on request',
  },
  {
    action: 'AI compliance gap analysis',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.COMPLIANCE_GAP_ANALYSIS],
    note: 'One framework, typically once a quarter',
  },
  {
    action: 'AI narrative scan report',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.AI_NARRATIVE_REPORT],
    note: 'Written summary of a scan, once or twice a month',
  },
  {
    action: 'Compliance evidence package',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.COMPLIANCE_EVIDENCE_SINGLE],
    note: 'One framework, audit-ready; usually quarterly',
  },
  {
    action: 'Multi-framework compliance cross-mapping',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.COMPLIANCE_CROSS_MAPPING],
    note: 'One run across every framework you hold',
  },
  {
    action: 'Compliance evidence package, multi-framework',
    cost: NINJA_CREDIT_COSTS[NinjaCreditAction.COMPLIANCE_EVIDENCE_MULTI],
    note: 'Every framework at once, typically once a quarter',
  },
];

/**
 * On-demand scans are priced by scan type and asset count rather than a flat
 * rate, so they get their own row rather than a single number.
 */
export interface ScanUsageTier {
  label: string;
  examples: string;
  baseCoins: number;
  coinsPerAsset: number;
}

export const SCAN_USAGE_TIERS: readonly ScanUsageTier[] = [
  {
    label: 'Discovery',
    examples: 'Host discovery, ping sweep, inventory collection',
    ...NINJA_SCAN_COST_RATES.discovery,
  },
  {
    label: 'Configuration & compliance',
    examples: 'CMMC, FedRAMP, TX-RAMP, CSF 2.0, SOC 2, GitHub',
    ...NINJA_SCAN_COST_RATES.light,
  },
  {
    label: 'Vulnerability & posture',
    examples: 'Basic and advanced network, web app, cloud, PCI',
    ...NINJA_SCAN_COST_RATES.standard,
  },
  {
    label: 'Authenticated & agent',
    examples: 'Credentialed patch audit, advanced agent scan',
    ...NINJA_SCAN_COST_RATES.deep,
  },
];

/** Included in every plan, never metered. Reassurance, not fine print. */
export const TOKEN_USAGE_INCLUDED: readonly string[] = [
  'Continuous scheduled scans, however many you run',
  'AI enrichment of scheduled scan findings',
  'The dashboard, findings, assets and compliance status',
  'Viewing and downloading evidence after it is generated',
  'Reports you have already generated',
  'Users, teams, scanners, integrations and notification settings',
];
